/**
 * Equipment Types and Categories for Freight Transportation
 * Used in Purchase Orders, Load Details, and Shipping Forms
 */

/**
 * Default equipment type used throughout the application
 * This is the standard default when no equipment type is specified
 */
export const DEFAULT_EQUIPMENT_TYPE = 'Dry Van';
export const DEFAULT_EQUIPMENT_TYPES = ['Dry Van'];

export interface EquipmentOption {
  id: string;
  name: string;
  description: string;
  category: string;
  maxWeight?: number;
  maxLength?: number;
  maxWidth?: number;
  maxHeight?: number;
  specialRequirements?: string[];
  // Compatibility with existing carrier system
  carrierType?: 'dry_van' | 'reefer' | 'flatbed' | 'step_deck' | 'box_truck' | 'other';
}

export interface EquipmentCategory {
  id: string;
  name: string;
  description: string;
  options: EquipmentOption[];
}

export const EQUIPMENT_CATEGORIES: EquipmentCategory[] = [
  {
    id: 'dry-van',
    name: 'Dry Van Equipment',
    description: 'Standard enclosed trailers for palletized, boxed, or crated goods',
    options: [
      {
        id: '53-dry-van',
        name: '53\' Dry Van Trailer',
        description: 'Standard enclosed trailer for palletized, boxed, or crated goods',
        category: 'dry-van',
        carrierType: 'dry_van',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: '48-dry-van',
        name: '48\' Dry Van Trailer',
        description: 'Common for regional freight or where smaller capacity is sufficient',
        category: 'dry-van',
        carrierType: 'dry_van',
        maxWeight: 80000,
        maxLength: 576,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: 'pup-trailer',
        name: 'Pup Trailers (28\'–32\')',
        description: 'Shorter trailers for LTL or city deliveries',
        category: 'dry-van',
        maxWeight: 80000,
        maxLength: 384,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: 'straight-truck',
        name: 'Straight Truck (Box Truck)',
        description: 'Rigid body truck for local/urban deliveries, usually 22–26\'',
        category: 'dry-van',
        maxWeight: 80000,
        maxLength: 312,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: 'cargo-van',
        name: 'Cargo Van',
        description: 'Small shipments, courier, and final-mile use',
        category: 'dry-van',
        maxWeight: 10000,
        maxLength: 240,
        maxWidth: 84,
        maxHeight: 120
      }
    ]
  },
  {
    id: 'flatbed',
    name: 'Flatbed Equipment',
    description: 'Open platform trailers for steel, lumber, pipe, and machinery',
    options: [
      {
        id: 'standard-flatbed-48',
        name: 'Standard Flatbed (48\')',
        description: 'Open platform for steel, lumber, pipe, and machinery',
        category: 'flatbed',
        maxWeight: 80000,
        maxLength: 576,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'standard-flatbed-53',
        name: 'Standard Flatbed (53\')',
        description: 'Open platform for steel, lumber, pipe, and machinery',
        category: 'flatbed',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'step-deck',
        name: 'Step Deck (Drop Deck)',
        description: 'Lower deck height for taller loads (max height ~10 ft)',
        category: 'flatbed',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'double-drop',
        name: 'Double Drop / Lowboy',
        description: 'For very tall or heavy machinery; deck drops twice for clearance',
        category: 'flatbed',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'extendable-flatbed',
        name: 'Extendable Flatbed / Stretch Trailer',
        description: 'Length-adjustable for oversized freight (up to ~80\')',
        category: 'flatbed',
        maxWeight: 80000,
        maxLength: 960,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'conestoga',
        name: 'Conestoga (Curtainside Flatbed)',
        description: 'Tarpaulin system covers freight while maintaining flatbed flexibility',
        category: 'flatbed',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'removable-gooseneck',
        name: 'Removable Gooseneck (RGN)',
        description: 'Detachable front for driving equipment onto deck',
        category: 'flatbed',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'heavy-haul',
        name: 'Heavy Haul / Multi-Axle',
        description: 'Extra axles for very heavy loads (>80,000 lbs GVW)',
        category: 'flatbed',
        maxWeight: 200000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      }
    ]
  },
  {
    id: 'reefer',
    name: 'Refrigerated Equipment (Reefers)',
    description: 'Temperature-controlled transport for food, pharmaceuticals, etc.',
    options: [
      {
        id: '53-reefer',
        name: '53\' Refrigerated Van',
        description: 'Temperature-controlled transport for food, pharmaceuticals, etc.',
        category: 'reefer',
        carrierType: 'reefer',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: '48-reefer',
        name: '48\' Reefer',
        description: 'Smaller variant, common for regional routes',
        category: 'reefer',
        maxWeight: 80000,
        maxLength: 576,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: 'straight-truck-reefer',
        name: 'Straight Truck Reefer',
        description: 'For local perishable delivery (groceries, catering, medical)',
        category: 'reefer',
        maxWeight: 80000,
        maxLength: 312,
        maxWidth: 102,
        maxHeight: 162
      }
    ]
  },
  {
    id: 'tanker',
    name: 'Tanker Equipment',
    description: 'Specialized tanks for liquids and bulk materials',
    options: [
      {
        id: 'dry-bulk-tanker',
        name: 'Dry Bulk Tanker',
        description: 'For cement, flour, sand, plastic pellets',
        category: 'tanker',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: 'liquid-tanker',
        name: 'Liquid Tanker',
        description: 'For milk, fuel, chemicals, or non-hazardous liquids',
        category: 'tanker',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 162
      },
      {
        id: 'chemical-tanker',
        name: 'Chemical Tanker (DOT 407/412)',
        description: 'Stainless steel or lined tanks for hazardous liquids',
        category: 'tanker',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 162,
        specialRequirements: ['Hazmat Certification', 'DOT Compliance']
      },
      {
        id: 'food-grade-tanker',
        name: 'Food Grade Tanker',
        description: 'Sanitary tanks for consumable liquids',
        category: 'tanker',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 162,
        specialRequirements: ['Food Grade Certification']
      }
    ]
  },
  {
    id: 'intermodal',
    name: 'Intermodal & Container Equipment',
    description: 'Equipment for ocean containers and rail transport',
    options: [
      {
        id: 'container-chassis-20',
        name: 'Container Chassis (20\')',
        description: 'Carries ocean containers from ports or rail yards',
        category: 'intermodal',
        maxWeight: 80000,
        maxLength: 240,
        maxWidth: 96,
        maxHeight: 120
      },
      {
        id: 'container-chassis-40',
        name: 'Container Chassis (40\')',
        description: 'Carries ocean containers from ports or rail yards',
        category: 'intermodal',
        maxWeight: 80000,
        maxLength: 480,
        maxWidth: 96,
        maxHeight: 120
      },
      {
        id: 'container-chassis-45',
        name: 'Container Chassis (45\')',
        description: 'Carries ocean containers from ports or rail yards',
        category: 'intermodal',
        maxWeight: 80000,
        maxLength: 540,
        maxWidth: 96,
        maxHeight: 120
      },
      {
        id: 'intermodal-trailer',
        name: 'Intermodal Trailers (TOFC/COFC)',
        description: 'Truck-rail compatible for double-stack or piggyback',
        category: 'intermodal',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'drayage-tractor',
        name: 'Drayage Tractor',
        description: 'Port or rail-yard specific day cab',
        category: 'intermodal',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      }
    ]
  },
  {
    id: 'specialty',
    name: 'Specialty Equipment',
    description: 'Specialized trailers for specific cargo types',
    options: [
      {
        id: 'auto-hauler',
        name: 'Auto Hauler',
        description: 'Single- or multi-level trailers for vehicles',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'livestock-trailer',
        name: 'Livestock Trailer',
        description: 'Ventilated trailer for animals',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120,
        specialRequirements: ['Livestock Certification']
      },
      {
        id: 'logging-trailer',
        name: 'Logging Trailer',
        description: 'Pole trailers for timber and tree-length logs',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'dump-trailer',
        name: 'Dump Trailer',
        description: 'For aggregates, soil, construction debris',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'hopper-bottom',
        name: 'Hopper Bottom Trailer',
        description: 'Bottom-dumping grain or feed hauler',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'end-dump',
        name: 'End Dump / Side Dump',
        description: 'Hydraulic lift trailers for bulk unloading',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'tank-wagon',
        name: 'Tank Wagon',
        description: 'Local fuel or chemical distribution',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'roll-off-truck',
        name: 'Roll-off Truck',
        description: 'For dumpsters and heavy waste containers',
        category: 'specialty',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      }
    ]
  },
  {
    id: 'power-units',
    name: 'Power Units (Tractors & Trucks)',
    description: 'Tractors and trucks for hauling trailers',
    options: [
      {
        id: 'day-cab-tractor',
        name: 'Day Cab Tractor',
        description: 'No sleeper, regional or local hauls',
        category: 'power-units',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'sleeper-cab-tractor',
        name: 'Sleeper Cab Tractor',
        description: 'Long-haul use; includes living space',
        category: 'power-units',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'heavy-haul-tractor',
        name: 'Heavy Haul Tractor',
        description: 'Reinforced frame for oversized loads',
        category: 'power-units',
        maxWeight: 200000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'medium-duty-truck',
        name: 'Medium-Duty Truck (Class 6–7)',
        description: 'Regional or local operations',
        category: 'power-units',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'light-duty-truck',
        name: 'Light-Duty Truck (Class 3–5)',
        description: 'Local delivery or final mile',
        category: 'power-units',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'pickup-truck',
        name: 'Pickup Truck (Gooseneck/Hotshot)',
        description: 'For expedited or LTL freight',
        category: 'power-units',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      }
    ]
  },
  {
    id: 'hotshot',
    name: 'Hotshot & Specialty Light Equipment',
    description: 'Light equipment for expedited and specialized deliveries',
    options: [
      {
        id: 'gooseneck-trailer',
        name: 'Gooseneck Trailer',
        description: 'Pulled by a pickup; ideal for expedited or rural routes',
        category: 'hotshot',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'dovetail-trailer',
        name: 'Dovetail Trailer',
        description: 'With ramps for easy loading',
        category: 'hotshot',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'deckover-trailer',
        name: 'Deckover / Tilt Deck Trailer',
        description: 'For small machinery or materials',
        category: 'hotshot',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'enclosed-car-hauler',
        name: 'Enclosed Car Hauler / Cargo Trailer',
        description: 'Protected high-value loads',
        category: 'hotshot',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      }
    ]
  },
  {
    id: 'emerging',
    name: 'Emerging / Alternative Equipment',
    description: 'New and alternative fuel equipment',
    options: [
      {
        id: 'electric-freight-tractor',
        name: 'Electric Freight Tractors',
        description: 'Zero-emission short-haul operations',
        category: 'emerging',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'cng-lng-truck',
        name: 'CNG / LNG Trucks',
        description: 'Natural gas-powered fleet options',
        category: 'emerging',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'autonomous-tractor',
        name: 'Autonomous Tractors',
        description: 'Pilot fleets for long-haul automation',
        category: 'emerging',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      },
      {
        id: 'hybrid-box-reefer',
        name: 'Hybrid Box or Reefer Trucks',
        description: 'Energy-efficient city distribution',
        category: 'emerging',
        maxWeight: 80000,
        maxLength: 636,
        maxWidth: 102,
        maxHeight: 120
      }
    ]
  }
];

/**
 * Get all equipment options flattened into a single array
 */
export const getAllEquipmentOptions = (): EquipmentOption[] => {
  return EQUIPMENT_CATEGORIES.flatMap(category => category.options);
};

/**
 * Get equipment options by category
 */
export const getEquipmentOptionsByCategory = (categoryId: string): EquipmentOption[] => {
  const category = EQUIPMENT_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.options : [];
};

/**
 * Get equipment option by ID
 */
export const getEquipmentOptionById = (equipmentId: string): EquipmentOption | undefined => {
  return getAllEquipmentOptions().find(option => option.id === equipmentId);
};

/**
 * Get equipment category by ID
 */
export const getEquipmentCategoryById = (categoryId: string): EquipmentCategory | undefined => {
  return EQUIPMENT_CATEGORIES.find(category => category.id === categoryId);
};

/**
 * Convert new equipment system to legacy carrier system
 */
export const toCarrierEquipmentType = (equipmentId: string): 'dry_van' | 'reefer' | 'flatbed' | 'step_deck' | 'box_truck' | 'other' => {
  const equipment = getEquipmentOptionById(equipmentId);
  return equipment?.carrierType || 'other';
};

/**
 * Convert legacy carrier system to new equipment system
 */
export const fromCarrierEquipmentType = (carrierType: 'dry_van' | 'reefer' | 'flatbed' | 'step_deck' | 'box_truck' | 'other'): EquipmentOption[] => {
  return getAllEquipmentOptions().filter(option => option.carrierType === carrierType);
};

/**
 * Get equipment options compatible with existing carrier system
 */
export const getCompatibleEquipmentOptions = (carrierType: 'dry_van' | 'reefer' | 'flatbed' | 'step_deck' | 'box_truck' | 'other'): EquipmentOption[] => {
  return getAllEquipmentOptions().filter(option => option.carrierType === carrierType);
};
